import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import type { ApplyTravelerCaptureDto } from '@bandido/shared/dtos';
import type {
  TravelerCaptureAnalysisResult,
  TravelerCaptureEvidence,
  TravelerCaptureProposal,
  TravelerCaptureSource,
} from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

export const capturePath = (caseId: number) => `copilot/cases/${caseId}/traveler-capture`;
export const captureKey = (caseId: number) => ['travelerCapture', caseId];

export const useCaptureSources = (caseId: number) =>
  useInfiniteQuery({
    queryKey: [...captureKey(caseId), 'sources'],
    queryFn: async ({ pageParam }) =>
      (
        await client.get<{
          enabled: boolean;
          items: TravelerCaptureSource[];
          nextBeforeId: number | null;
        }>(`${capturePath(caseId)}/sources`, { params: pageParam ? { beforeId: pageParam } : {} })
      ).data,
    getNextPageParam: (page) => page.nextBeforeId ?? undefined,
  });

export const useCaptureProposals = (caseId: number, page = 1, enabled = true) =>
  useQuery({
    queryKey: [...captureKey(caseId), 'proposals', page],
    queryFn: async () =>
      (
        await client.get<{ items: TravelerCaptureProposal[]; total: number }>(`${capturePath(caseId)}/proposals`, {
          params: { page },
        })
      ).data,
    refetchInterval: 60_000,
    enabled,
  });

export const useCaptureEvidence = (caseId: number, travelerId: string, page: number) =>
  useQuery({
    queryKey: [...captureKey(caseId), 'evidence', travelerId, page],
    queryFn: async () =>
      (
        await client.get<{ items: TravelerCaptureEvidence[]; total: number }>(
          `${capturePath(caseId)}/travelers/${travelerId}/evidence`,
          { params: { page } },
        )
      ).data,
  });

export const useAnalyzeCapture = (caseId: number) => {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async (sourceIds: string[]) =>
      (
        await client.post<TravelerCaptureAnalysisResult>(
          `${capturePath(caseId)}/analyze`,
          { sourceIds },
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: () => cache.invalidateQueries([...captureKey(caseId), 'proposals']),
  });
};

export const useDismissCapture = (caseId: number) => {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.post(`${capturePath(caseId)}/proposals/${id}/dismiss`),
    onSuccess: () => cache.invalidateQueries([...captureKey(caseId), 'proposals']),
  });
};

export const useApplyCapture = (caseId: number, proposalId: string) => {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApplyTravelerCaptureDto) =>
      (await client.post(`${capturePath(caseId)}/proposals/${proposalId}/apply`, input)).data,
    onSuccess: () =>
      Promise.all([
        cache.invalidateQueries(captureKey(caseId)),
        ...[QueryKeys.getCaseTravelers, QueryKeys.getCaseTravelerSuggestions, QueryKeys.getTravelerDocumentMatches].map(
          (key) => cache.invalidateQueries([key, caseId]),
        ),
      ]),
  });
};
